/**
 * Constitutional gate for agentic simulation steps (spec §8).
 *
 * Three-lane safety evaluation:
 *   BLOCK  — HIGH_RISK domains (security, tax, subscription)
 *   WARN   — IRREVERSIBLE entity writes (Account, PlaidConnection, …)
 *   ALLOW  — everything else
 *
 * 14 constitutional principles (P1–P14) are checked in order; first
 * violation determines the tier. All principles run to collect the full
 * violation list even after the tier is determined.
 * Target: <45 ms p99 (pure sync, no I/O).
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import type { AgentGateResult, EnrichedSimulationStep, SafetyTier } from './simulator.js';
import { entityById } from './selectors.js';

// ─── Domain + entity classification tables ────────────────────────────────────

const HIGH_RISK_DOMAINS = new Set(['security', 'tax', 'subscription', 'billing']);

const IRREVERSIBLE_ENTITIES = new Set([
  'Account',
  'PlaidConnection',
  'StripeSubscription',
  'ConsentHistory',
  'AuditLog',
  'UserProfile',
]);

// ─── 14 Constitutional principles ────────────────────────────────────────────

interface Principle {
  id: string;
  label: string;
  check(step: EnrichedSimulationStep, manifest: AtlasManifest): string | null;
}

const PRINCIPLES: Principle[] = [
  {
    id: 'P1',
    label: 'No write to security domain without explicit user intent',
    check(step, manifest) {
      for (const entityId of step.writes) {
        const entity = entityById(manifest, entityId);
        if (!entity) continue;
        const domain = manifest.domains.find((d) => d.id === entity.domain);
        if (domain && HIGH_RISK_DOMAINS.has(domain.id) && step.actor !== 'user') {
          return `Agentic write to high-risk domain '${domain.id}'`;
        }
      }
      return null;
    },
  },
  {
    id: 'P2',
    label: 'Irreversible entity writes must be user-confirmed',
    check(step) {
      const hit = step.writes.find((w) => IRREVERSIBLE_ENTITIES.has(w));
      if (hit && step.actor !== 'user') return `Agentic write to irreversible entity '${hit}'`;
      return null;
    },
  },
  {
    id: 'P3',
    label: 'No cross-user data access',
    check(step) {
      if (step.source.toLowerCase().includes('bulk') && step.writes.length > 5) {
        return 'Bulk write pattern may span multiple users';
      }
      return null;
    },
  },
  {
    id: 'P4',
    label: 'Financial mutations require audit trail',
    check(step, manifest) {
      const financialDomains = new Set(['transactions', 'accounts', 'budgets', 'goals']);
      for (const entityId of step.writes) {
        const entity = entityById(manifest, entityId);
        if (!entity) continue;
        const domain = manifest.domains.find((d) => d.id === entity.domain);
        if (domain && financialDomains.has(domain.id)) {
          if (!step.eventsFired.some((e) => e.includes('audit') || e.includes('log'))) {
            return `Financial write to '${entityId}' without audit event`;
          }
        }
      }
      return null;
    },
  },
  {
    id: 'P5',
    label: 'No PII in event payloads',
    check(step) {
      const piiPatterns = ['email', 'phone', 'ssn', 'dob', 'address', 'name'];
      const source = step.source.toLowerCase();
      if (piiPatterns.some((p) => source.includes(p) && step.actor !== 'user')) {
        return `Possible PII in event source '${step.source}'`;
      }
      return null;
    },
  },
  {
    id: 'P6',
    label: 'Scheduled CF must not write to subscription tier',
    check(step) {
      if (step.cfKind === 'scheduled' && step.writes.some((w) => w.toLowerCase().includes('tier'))) {
        return 'Scheduled CF writing subscription tier';
      }
      return null;
    },
  },
  {
    id: 'P7',
    label: 'Callable CF must not fire events that trigger irreversible writes',
    check(step) {
      if (step.cfKind === 'callable') {
        const dangerous = step.eventsFired.filter(
          (e) => e.includes('delete') || e.includes('purge') || e.includes('terminate'),
        );
        if (dangerous.length > 0) return `Callable fires dangerous events: ${dangerous.join(', ')}`;
      }
      return null;
    },
  },
  {
    id: 'P8',
    label: 'No more than 3 downstream CF hops for agentic actions',
    check(step) {
      if (step.hop > 3 && step.actor !== 'user' && step.actor !== 'event-bus') {
        return `CF hop depth ${step.hop} exceeds agentic limit of 3`;
      }
      return null;
    },
  },
  {
    id: 'P9',
    label: 'Tax domain writes require human approval',
    check(step, manifest) {
      for (const entityId of step.writes) {
        const entity = entityById(manifest, entityId);
        if (!entity) continue;
        const domain = manifest.domains.find((d) => d.id === entity.domain);
        if (domain?.id === 'tax' && step.actor !== 'user') {
          return `Agentic write to tax domain entity '${entityId}'`;
        }
      }
      return null;
    },
  },
  {
    id: 'P10',
    label: 'Stripe webhooks must validate tier before writing',
    check(step) {
      if (step.source.toLowerCase().includes('stripe') && step.writes.some((w) => w.includes('Tier'))) {
        if (!step.eventsFired.some((e) => e.includes('validated') || e.includes('verified'))) {
          return 'Stripe webhook tier write without validation event';
        }
      }
      return null;
    },
  },
  {
    id: 'P11',
    label: 'No WAL drain during active circuit breaker',
    check(step) {
      if (
        step.source.toLowerCase().includes('wal') &&
        step.eventsFired.some((e) => e.includes('circuit_breaker'))
      ) {
        return 'WAL drain triggered while circuit breaker is active';
      }
      return null;
    },
  },
  {
    id: 'P12',
    label: 'MCP tool writes must be quota-checked',
    check(step) {
      if (step.source.toLowerCase().includes('mcp') && step.writes.length > 0) {
        if (!step.eventsFired.some((e) => e.includes('quota'))) {
          return 'MCP tool write without quota check event';
        }
      }
      return null;
    },
  },
  {
    id: 'P13',
    label: 'Agent insight writes must have user consent',
    check(step) {
      if (step.source.toLowerCase().includes('insight') && step.actor !== 'user') {
        if (!step.eventsFired.some((e) => e.includes('consent'))) {
          return 'Agent insight write without consent event';
        }
      }
      return null;
    },
  },
  {
    id: 'P14',
    label: 'No recursive CF invocation chains beyond visited-set',
    check(step) {
      if (step.cfsInvoked.some((cf) => step.source === cf)) {
        return `CF '${step.source}' invokes itself`;
      }
      return null;
    },
  },
];

// ─── Public API ───────────────────────────────────────────────────────────────

function worstTier(violations: string[], step: EnrichedSimulationStep, manifest: AtlasManifest): SafetyTier {
  // Check BLOCK conditions first
  for (const entityId of step.writes) {
    const entity = entityById(manifest, entityId);
    if (!entity) continue;
    const domain = manifest.domains.find((d) => d.id === entity.domain);
    if (domain && HIGH_RISK_DOMAINS.has(domain.id) && step.actor !== 'user') return 'BLOCK';
  }
  // Check WARN conditions
  for (const entityId of step.writes) {
    if (IRREVERSIBLE_ENTITIES.has(entityId) && step.actor !== 'user') return 'WARN';
  }
  if (violations.length > 0) return 'WARN';
  return 'ALLOW';
}

/**
 * Evaluates all 14 constitutional principles for a single enriched step.
 * Returns ALLOW when no principles fire.
 */
export function evaluateAgentGate(
  step: EnrichedSimulationStep,
  manifest: AtlasManifest,
): AgentGateResult {
  const principleViolations: string[] = [];
  const reasons: string[] = [];

  for (const principle of PRINCIPLES) {
    const result = principle.check(step, manifest);
    if (result) {
      principleViolations.push(principle.id);
      reasons.push(result);
    }
  }

  const tier = worstTier(principleViolations, step, manifest);
  const reason = reasons.length > 0 ? reasons[0] : 'All principles satisfied';

  return { tier, reason, principleViolations };
}
